import React, { forwardRef, useContext } from 'react';
import { View, Text, colors } from '@fairygodboss/ui';
import { buildImagePath } from '../../../../JobComponents/Company/Image/logo';
import SocialShareBarV2 from '../../../../CallToAction/SocialShareBarV2/SocialShareBarV2';
import SVGFromCloudinary from '../../../../Elements/SVGFromCloudinary/SVGFromCloudinary';
import { getFormattatedDayTimestamp } from '../../../../Utilities/TimeFunctions';
import { PageContext } from '../../../../../contexts/ContextCreator';

const StarSVG = () => {
    return (
        <svg
            css={{
                position: 'relative',
                top: '2px',
                paddingRight: '4px',
            }}
            width="16"
            height="15"
            viewBox="0 0 16 15"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M8 0L10.1678 5.01635L15.6085 5.52786L11.5075 9.13965L12.7023 14.4721L8 11.688L3.29772 14.4721L4.4925 9.13965L0.391548 5.52786L5.83225 5.01635L8 0Z"
                fill="#B394F2"
            />
        </svg>
    );
};

const Socials = ({ url }) => {
    return (
        <View width="147px">
            <SocialShareBarV2
                urlToShare={url}
                addToMetrics={{
                    community_content_location: 'singleContent',
                }}
            />
        </View>
    );
};

export const Author = forwardRef(
    (
        {
            author,
            contentModifiedDate,
            didEdit,
            hash,
            type,
            windowWidth,
            content,
            isCommunityDiscussionType,
            ...others
        },
        ref
    ) => {
        const {
            image,
            username,
            karmaScore,
            credential,
            publicUrl,
            userCommunityData,
        } = author;
        const isVip = userCommunityData?.isVip
            ? userCommunityData.isVip
            : false;
        const src = buildImagePath(image?.src, 'img', {
            img: 'dpr_2.0,c_scale,g_center,r_max,q_auto,w_48,h_48',
        });
        const formatedDate = getFormattatedDayTimestamp(contentModifiedDate);
        const edited = didEdit ? '(Edited)' : '';
        const {
            pageProps: { url },
        } = useContext(PageContext);
        const { host } = url;
        return (
            <>
                <View
                    ref={ref}
                    pt="28px"
                    display="flex"
                    css={{
                        img: {
                            clipPath: `circle(24px)`,
                            height: '48px',
                            width: '48px',
                        },
                    }}
                    {...others}
                >
                    <View
                        display="flex"
                        justifyContent="space-between"
                        flexDirection={{ _: 'column', sm: 'row' }}
                        width="100%"
                        alignItems="start"
                    >
                        <View display="flex" flexDirection="column">
                            <View display="flex" flex="1">
                                <View
                                    as={publicUrl ? 'a' : 'div'}
                                    href={publicUrl}
                                    target="_self"
                                    css={{
                                        position: 'relative',
                                    }}
                                >
                                    <View
                                        mr="16px"
                                        as="img"
                                        src={src}
                                        alt={image?.alt || 'Profile Picture'}
                                    />
                                    {isVip === 1 && (
                                        <View
                                            css={{
                                                img: {
                                                    width: '16px',
                                                    position: 'absolute',
                                                    left: '32px',
                                                    top: '16px',
                                                },
                                            }}
                                        >
                                            <SVGFromCloudinary
                                                cloudinaryID="/v1/SVGs/VIP_LARGE.svg"
                                                transformString="/c_scale,g_center,r_max,w_8,h_8"
                                            />
                                        </View>
                                    )}
                                </View>
                                <View
                                    display="flex"
                                    flexWrap="wrap"
                                    flexDirection="column"
                                >
                                    <View
                                        display="flex"
                                        flexDirection={{
                                            _: 'column',
                                            lg: 'row',
                                        }}
                                        alignItems={{
                                            _: 'start',
                                            lg: 'center',
                                        }}
                                        flexWrap="wrap"
                                        // flex="1"
                                    >
                                        <View
                                            display="flex"
                                            {...(isCommunityDiscussionType && {
                                                itemProp: 'author',
                                                itemScope: 'itemscope',
                                                itemType:
                                                    'https://schema.org/Person',
                                            })}
                                        >
                                            {isCommunityDiscussionType && (
                                                <View
                                                    as="a"
                                                    href={publicUrl}
                                                    itemProp="url"
                                                />
                                            )}
                                            <Text
                                                as="p"
                                                fontWeight="700"
                                                margin="0"
                                                textAlign="left"
                                                variant="labelMd"
                                                letterSpacing="-0.4px"
                                                lineHeight="18px"
                                                color={colors.neutral[100]}
                                                {...(isCommunityDiscussionType && {
                                                    itemProp: 'name',
                                                })}
                                            >
                                                {username}
                                                {karmaScore ? (
                                                    <Text
                                                        pr={{
                                                            _: '0',
                                                            lg: '12px',
                                                        }}
                                                        pl={{
                                                            _: '4px',
                                                            md: '12px',
                                                        }}
                                                        color={
                                                            colors.neutral[100]
                                                        }
                                                        variant="labelMd"
                                                        fontWeight="400"
                                                    >
                                                        <StarSVG />
                                                        {karmaScore}
                                                    </Text>
                                                ) : null}
                                            </Text>
                                        </View>
                                        <Text
                                            pt={{ _: '2px', lg: '0px' }}
                                            letterSpacing="-0.4px"
                                            color={colors.neutral[100]}
                                            variant="labelMd"
                                        >
                                            {credential}
                                        </Text>
                                    </View>
                                    <View flex="1" mt={{ _: '0px', lg: '4px' }}>
                                        <Text
                                            color={colors.neutral[70]}
                                            variant="labelMd"
                                            {...(isCommunityDiscussionType && {
                                                itemProp: 'datePublished',
                                                dateTime: new Date(
                                                    contentModifiedDate
                                                )
                                                    .toISOString()
                                                    .split('T')[0],
                                            })}
                                        >
                                            {`${formatedDate} ${edited}`}
                                        </Text>
                                    </View>
                                </View>
                            </View>
                        </View>
                        <View
                            pl={{ _: '0px', sm: '8px' }}
                            pt={{ _: '16px', sm: '0px' }}
                        >
                            <Socials
                                hash={hash}
                                type={type}
                                windowWidth={windowWidth}
                                url={`${host}${content?.contentUrl}`}
                            />
                        </View>
                    </View>
                </View>
            </>
        );
    }
);
